import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Switch } from "../ui/switch";
import { useTranslation } from "../../i18n";
import PushTemplateEditor from "./PushTemplateEditor";
import CronPicker from "./CronPicker";

/**
 * Rendered by PushTemplateEditor rather than on its own row: the body template
 * cannot be composed without the title beside it, and the preview shows both.
 */
const FIELD_OWNED_BY_PUSH_EDITOR = "push_body_template";

/**
 * Dynamic form that renders fields based on the selected trigger type's schema.
 * Fields come from the already-loaded trigger types — no per-instance refetch.
 *
 * Every field in the schema is rendered. Some are routed to a bespoke control
 * by `renderField` (the push templates get a placeholder-aware editor with a
 * server-rendered preview, `schedule_cron` gets a CronPicker) — those must
 * still be *listed* here, otherwise the control is never reached. A previous
 * version filtered them out by name on the assumption that `renderField`
 * handled them, which silently removed the title, body and schedule inputs
 * from every form.
 */
export default function CampaignForm({
  fields,
  config,
  onChange,
  disabled = false,
  triggerType,
  placeholders = [],
  campaignId = null,
  segmentOptions = [],
}) {
  const { t } = useTranslation();

  const handleChange = (name, value) => {
    onChange({ ...config, [name]: value });
  };

  if (!fields || fields.length === 0) {
    return (
      <div className="text-c-gray-400 text-sm">
        {t("campaign.selectTrigger")}
      </div>
    );
  }

  const labelFor = (name) => fields.find((f) => f.name === name)?.label;
  const defaultFor = (name) => fields.find((f) => f.name === name)?.default;

  // The backend already dedupes this list, but a duplicate field name would
  // render two inputs sharing one React key — which React rejects outright
  // ("Encountered two children with the same key"). Dedupe here too so a
  // stale backend or a future schema change degrades to one control instead
  // of a broken form.
  const uniqueFields = [];
  const seen = new Set();
  for (const f of fields) {
    if (seen.has(f.name)) continue;
    seen.add(f.name);
    uniqueFields.push(f);
  }

  return (
    <div className="space-y-4">
      {uniqueFields
        .filter((f) => f.name !== FIELD_OWNED_BY_PUSH_EDITOR)
        .map((field) => (
          <div key={field.name}>
            {renderField({
              field,
              // The schema default is resolved only when the key is absent: a
              // key the operator cleared holds null, and must render as empty
              // rather than snapping back to the default.
              value: field.name in config ? config[field.name] : field.default,
              onChange: handleChange,
              disabled,
              triggerType,
              placeholders,
              campaignId,
              config,
              bodyLabel: labelFor(FIELD_OWNED_BY_PUSH_EDITOR),
              bodyDefault: defaultFor(FIELD_OWNED_BY_PUSH_EDITOR),
              segmentOptions,
              allFields: uniqueFields,
              t,
            })}
          </div>
        ))}
    </div>
  );
}

function renderField({
  field,
  value,
  onChange,
  disabled,
  triggerType,
  placeholders,
  campaignId,
  config,
  bodyLabel,
  bodyDefault,
  segmentOptions,
  allFields = [],
  t,
}) {
  // The push title and body are edited together: one notification, one
  // preview. Anchor the pair on the title field so the pair renders once.
  if (field.name === "push_title_template") {
    return (
      <PushTemplateEditor
        config={config}
        onChange={onChange}
        defaults={{ title: field.default, body: bodyDefault }}
        titleLabel={field.label}
        bodyLabel={bodyLabel}
        disabled={disabled}
        triggerType={triggerType}
        placeholders={placeholders}
        campaignId={campaignId}
      />
    );
  }

  switch (field.fieldType) {
    case "cron":
      return (
        <CronPicker
          value={value ?? ""}
          onChange={(v) => onChange(field.name, v)}
          disabled={disabled}
        />
      );

    case "number":
      return (
        <>
          <Label>{field.label}</Label>
          <Input
            // A text input, not `type="number"`: the browser spinner wrote
            // -20 into the field. The numeric meaning is kept on commit (see
            // handleNumberChange/commitNumber) and again on save; a cleared
            // field stays empty because the value above resolves the schema
            // default only when the key is absent.
            type="text"
            inputMode="numeric"
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) =>
              handleNumberChange(e, (n) => onChange(field.name, n))
            }
            onBlur={(e) =>
              commitNumber(e, field, (n) => onChange(field.name, n))
            }
          />
          {field.description && (
            <p className="text-c-gray-400 text-xs mt-1">{field.description}</p>
          )}
        </>
      );
    case "text":
      return (
        <>
          <Label>{field.label}</Label>
          <Input
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
          />
          {field.description && (
            <p className="text-c-gray-400 text-xs mt-1">{field.description}</p>
          )}
        </>
      );
    case "textarea":
      return (
        <>
          <Label>{field.label}</Label>
          <textarea
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
            rows={4}
            className="flex w-full rounded-lg border border-c-gray-400/30 bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-0 font-mono resize-y"
          />
          {field.description && (
            <p className="text-c-gray-400 text-xs mt-1">{field.description}</p>
          )}
        </>
      );
    case "toggle":
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Switch
              checked={!!value}
              disabled={disabled}
              onCheckedChange={(checked) => onChange(field.name, checked)}
            />
            <Label>{field.label}</Label>
          </div>
          {field.description && (
            <p className="text-c-gray-400 text-xs">{field.description}</p>
          )}
          {field.name === "ab_enabled" && (
            <ABVariantNote fields={allFields} enabled={!!value} t={t} />
          )}
        </div>
      );
    case "select":
      return (
        <>
          <Label>{field.label}</Label>
          <Select
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
          >
            {selectOptions(field, segmentOptions).map((opt) => (
              <option key={opt} value={opt}>
                {selectOptionLabel(field, opt, t)}
              </option>
            ))}
          </Select>
          {field.description && (
            <p className="text-c-gray-400 text-xs mt-1">{field.description}</p>
          )}
        </>
      );
    default:
      return (
        <>
          <Label>{field.label}</Label>
          <Input
            disabled={disabled}
            value={value ?? ""}
            onChange={(e) => onChange(field.name, e.target.value)}
          />
        </>
      );
  }
}

/**
 * Options for a select field.
 *
 * A field may be widened at runtime: `segment_name` ships with only the
 * built-in segments, but the operator can define more, so the live list from
 * the API is unioned with the declared options. The declared options come
 * first so a stored value is never silently reordered out of view.
 */
function selectOptions(field, extraOptions) {
  const declared = field.options || [];
  if (!extraOptions || extraOptions.length === 0) return declared;
  return [...new Set([...declared, ...extraOptions])];
}

/**
 * Label for a select option.
 *
 * The audience picker's empty value is "no segment", which must read as the
 * audience it actually means — every opted-in customer with a device — rather
 * than as a blank row.
 */
function selectOptionLabel(field, option, t) {
  if (option === "" && field.name === "segment_name") {
    return t("campaign.segmentAllOption");
  }
  return option;
}

/**
 * Normalise a number input before React re-renders it.
 *
 * Assigning `e.target.value` first means the browser never paints "05" while
 * React's controlled value catches up, and an empty field stays empty instead
 * of being coerced to 0 — which previously made "leave at 0" and "clear this"
 * indistinguishable.
 *
 * A lone "-" is committed as-is so the sign survives the round trip to React;
 * `commitNumber` turns it into a value (or nothing) on blur.
 */
function handleNumberChange(event, commit) {
  const raw = event.target.value;
  if (raw === "") {
    commit(null);
    return;
  }
  if (raw === "-") {
    commit(raw);
    return;
  }
  if (!/^-?\d+$/.test(raw)) {
    return;
  }
  const parsed = Number(raw);
  event.target.value = String(parsed);
  commit(parsed);
}

/**
 * Commit a number field on blur, clamped to the range its schema declares.
 *
 * The clamp is what stops a negative holdout percent (holds out nobody) or a
 * negative pacing delay from being saved, and it is applied to the value the
 * operator sees so the field never keeps text the config no longer holds.
 */
function commitNumber(event, field, commit) {
  const raw = String(event.target.value ?? "").trim();
  if (raw === "" || raw === "-") {
    event.target.value = "";
    commit(null);
    return;
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    event.target.value = "";
    commit(null);
    return;
  }
  const clamped = clampNumber(parsed, field);
  event.target.value = String(clamped);
  commit(clamped);
}

/**
 * What the B arm of an A/B test actually varies, shown beside the switch.
 *
 * Turning the switch on used to reveal nothing: the variant fields exist only
 * on some triggers (lost_customer_bonus has variant_b_bonus), and on the rest
 * both arms receive identical text while the switch still claims a test. The
 * note names the B-side fields when they exist, and says plainly when this
 * trigger has nothing to vary.
 */
function ABVariantNote({ fields, enabled, t }) {
  const variantFields = (fields || []).filter((f) => /^variant_b/.test(f.name));
  if (variantFields.length > 0) {
    return (
      <p className="text-c-gray-400 text-xs">
        {t("campaign.abVariants", {
          fields: variantFields.map((f) => f.label).join(", "),
        })}
      </p>
    );
  }
  return (
    <p className={`text-xs ${enabled ? "text-amber-400" : "text-c-gray-400"}`}>
      {t("campaign.abNothingToVary")}
    </p>
  );
}

/**
 * Clamp a number to the min/max a schema field declares, when it has them.
 *
 * The API serialises `ConfigField` through a camelCase model, so the bounds
 * arrive as `minValue`/`maxValue` — reading the snake_case names left both
 * bounds undefined and the clamp inert.
 */
export function clampNumber(value, field) {
  let result = Math.trunc(value);
  if (typeof field?.minValue === "number") {
    result = Math.max(field.minValue, result);
  }
  if (typeof field?.maxValue === "number") {
    result = Math.min(field.maxValue, result);
  }
  return result;
}
